import { Alert, Button, Center, Group, Loader, Paper, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconAlertCircle,
  IconBuildingBank,
  IconCreditCard,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useTenant } from 'app/TenantValidator';
import { Panel } from 'components/Panel';
import { i18nKeys, useTranslation } from 'locales';

import { type PaymentConfiguration, useLoadPaymentConfiguration } from '../api';
import { StripeCardModal } from './StripeCardModal';
import { TwikeyModal } from './TwikeyModal';

const CardSummary = ({
  cardInformation,
}: {
  cardInformation: PaymentConfiguration['cardInformation'];
}) => {
  const { last4Digits, holderName, expiryMonth, expiryYear, cardBrand } = cardInformation;

  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap">
          <IconCreditCard size={28} stroke={1.5} />
          <div>
            <Text fw={600} tt="capitalize">
              {cardBrand ?? 'Card'} •••• {last4Digits}
            </Text>
            {holderName && (
              <Text size="sm" c="dimmed">
                {holderName}
              </Text>
            )}
          </div>
        </Group>
        {expiryMonth && expiryYear && (
          <Text size="sm" c="dimmed">
            {expiryMonth}/{expiryYear}
          </Text>
        )}
      </Group>
    </Paper>
  );
};

export const PaymentMethodCard = () => {
  const { t } = useTranslation();
  const tenant = useTenant();
  const { paymentConfiguration, isPaymentConfigurationLoading } = useLoadPaymentConfiguration();

  // Resellers bill their own clients: no self-service payment method here.
  if (tenant?.customBranding) return null;

  const openStripeModal = () =>
    modals.open({
      title: t(i18nKeys.CREDIT_CARD),
      size: 'md',
      centered: true,
      children: <StripeCardModal />,
    });

  const openTwikeyModal = () =>
    modals.open({
      title: t(i18nKeys.ONBOARDING[1].DIRECT_DEBIT.TITLE),
      size: 'md',
      centered: true,
      children: <TwikeyModal />,
    });

  const renderBody = () => {
    if (isPaymentConfigurationLoading || !paymentConfiguration) {
      return (
        <Center h={120}>
          <Loader />
        </Center>
      );
    }

    const { paymentMethod, cardInformation, directBillingByIntegrator } = paymentConfiguration;

    return (
      <Stack gap="md">
        {paymentMethod === null && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />}>
            {t(i18nKeys.NO_PAYMENT_METHOD)}
          </Alert>
        )}

        {paymentMethod === 'twikey' && (
          <Group gap="sm">
            <IconBuildingBank size={24} stroke={1.5} />
            <Text>{t(i18nKeys.SETTINGS.PACKAGE.SEPA_MANDATE_VIA_TWIKEY)}</Text>
          </Group>
        )}

        {cardInformation.canEditCard && (
          <>
            <CardSummary cardInformation={cardInformation} />
            <Group justify="flex-end">
              <Button
                variant="light"
                leftSection={<IconCreditCard size={18} />}
                onClick={openStripeModal}
              >
                {t(i18nKeys.SETTINGS.PACKAGE.EDIT_BANKING_INFO)}
              </Button>
            </Group>
          </>
        )}

        {paymentMethod === null && (
          <Group>
            <Button
              color="green"
              leftSection={<IconBuildingBank size={18} />}
              onClick={openTwikeyModal}
            >
              {t(i18nKeys.ONBOARDING[1].DIRECT_DEBIT.TITLE)}
            </Button>
            <Button
              variant="light"
              leftSection={<IconCreditCard size={18} />}
              onClick={openStripeModal}
            >
              {t(i18nKeys.CREDIT_CARD)}
            </Button>
          </Group>
        )}

        {directBillingByIntegrator && (
          <Alert color="blue" variant="light" icon={<IconInfoCircle size={18} />}>
            {t(i18nKeys.SETTINGS.PACKAGE.PAYMENT_BY_INTEGRATOR)}
          </Alert>
        )}
      </Stack>
    );
  };

  return (
    <Panel title={t(i18nKeys.SETTINGS.PACKAGE.PAYMENT_METHOD)} withBorder fullHeight>
      {renderBody()}
    </Panel>
  );
};
